import { useEffect, createContext, useContext } from 'react'
import './App.css'
import Pages from "@/pages/index.jsx"
import { Toaster } from "sonner"
import { AuthProvider } from "@/lib/AuthContext"
import { ThemeProvider } from "@/lib/ThemeContext"
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import MaintenanceGuard from "@/components/MaintenanceGuard"
import LicenseGuard from "@/components/LicenseGuard"
import { logger } from "@/lib/logger"
import { captureException, setTag } from "@/lib/sentry"
import {
  checkCacheVersion,
  detectCorruptedSession,
  clearAllCaches,
  hardReset
} from "@/lib/cacheManager"

// Check cache version and clear if needed BEFORE React renders
// This runs synchronously on module load
const cacheWasCleared = checkCacheVersion();
if (cacheWasCleared) {
  logger.debug('Cache was cleared due to version update');
}

// Detect any corrupted session data
const corruptedData = detectCorruptedSession();
if (corruptedData) {
  logger.warn('Clearing corrupted session data...');
  clearAllCaches();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      onError: (error, variables, context) => {
        // Capture mutation errors in Sentry with context
        captureException(error, {
          type: 'mutation_error',
          variables: variables,
        });
      },
    },
  },
})

// Context to expose queryClient for cache clearing
const QueryClientContext = createContext(null);
export const useAppQueryClient = () => useContext(QueryClientContext);

function App() {
  // Set up global error handler to catch unhandled React Query errors
  useEffect(() => {
    // Debounce to prevent multiple rapid resets
    let resetTimeout = null;

    const handleGlobalError = (event) => {
      const error = event.error || event.reason;
      if (!error) return;

      // Skip hard reset when we are clearing the session ourselves (e.g. after 15s timeout).
      // 401s/auth errors that fire in that window would otherwise trigger a full reload and interrupt login.
      if (typeof window !== 'undefined' && window.__kconnect_clearing_session) {
        logger.debug('Skipping hard reset - session clear in progress');
        return;
      }

      const errorMessage = (error.message || '').toLowerCase();
      const errorCode = (error.code || '').toLowerCase();

      // Check for auth-related errors that indicate stale session
      const isAuthError =
        errorMessage.includes('jwt') ||
        errorMessage.includes('401') ||
        errorMessage.includes('session') ||
        errorMessage.includes('invalid refresh token') ||
        errorMessage.includes('refresh_token') ||
        errorMessage.includes('token expired') ||
        errorMessage.includes('unauthorized') ||
        errorCode === 'pgrst301' ||
        errorCode === '401';

      if (isAuthError) {
        logger.error('Detected auth error in global handler:', error.message);

        // Debounce the reset to prevent multiple resets
        if (resetTimeout) clearTimeout(resetTimeout);
        resetTimeout = setTimeout(() => {
          logger.debug('Triggering hard reset due to auth error');
          hardReset();
        }, 500);
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleGlobalError);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleGlobalError);
      if (resetTimeout) clearTimeout(resetTimeout);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <QueryClientContext.Provider value={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <MaintenanceGuard>
              <LicenseGuard>
                <Pages />
              </LicenseGuard>
            </MaintenanceGuard>
          </ThemeProvider>
          <Toaster
            position="top-center"
            offset="env(safe-area-inset-top, 16px)"
            toastOptions={{
              style: {
                marginTop: 'max(env(safe-area-inset-top), 16px)',
              },
            }}
          />
        </AuthProvider>
      </QueryClientContext.Provider>
    </QueryClientProvider>
  )
}

export default App
